import { useContext, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Box, Button, IconButton, Popover, Tooltip, Typography } from '@mui/material';
import NotificationsNoneRoundedIcon from '@mui/icons-material/NotificationsNoneRounded';
import ReceiptRoundedIcon from '@mui/icons-material/ReceiptRounded';
import UserContext from 'src/context/UserContext';
import { Role } from 'src/Role';
import { brand } from 'src/theme/brand';
import { useNavBadges } from '../NavBadgesContext';

export const headerIconButtonSx = {
  width: 40,
  height: 40,
  borderRadius: '12px',
  backgroundColor: brand.surface,
  color: brand.ink,
  '&:hover': { backgroundColor: brand.subtle }
};

/** Work waiting for the signed-in admin (bank transfers to review). */
function Notifications() {
  const [user] = useContext(UserContext);
  const { pendingBankTransfers = 0 } = useNavBadges();
  const navigate = useNavigate();
  const anchor = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);

  const reviewPath =
    user?.role === Role.CUSTOMER_SUPPORT ? '/customer-support/bank-transfers' : '/billing/bank-transfers';
  if (user?.role !== Role.SUPER_ADMIN && user?.role !== Role.CUSTOMER_SUPPORT) return null;

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton ref={anchor} onClick={() => setOpen(true)} sx={headerIconButtonSx} aria-label="Notifications">
          <Badge
            variant="dot"
            invisible={pendingBankTransfers === 0}
            sx={{ '& .MuiBadge-dot': { backgroundColor: brand.chartOrange, border: `2px solid ${brand.surface}`, width: 10, height: 10, borderRadius: '50%' } }}
          >
            <NotificationsNoneRoundedIcon sx={{ fontSize: 20 }} />
          </Badge>
        </IconButton>
      </Tooltip>
      <Popover
        open={open}
        anchorEl={anchor.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { mt: 1, width: 300, borderRadius: '16px', p: 2, boxShadow: '0 16px 40px rgba(22,25,22,0.12)' } }}
      >
        <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 1.5 }}>Notifications</Typography>
        {pendingBankTransfers === 0 ? (
          <Typography sx={{ fontSize: 13, color: brand.muted }}>You're all caught up.</Typography>
        ) : (
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: brand.subtle,
                flexShrink: 0
              }}
            >
              <ReceiptRoundedIcon sx={{ fontSize: 19 }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>
                {pendingBankTransfers} bank transfer{pendingBankTransfers === 1 ? '' : 's'} to review
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: brand.muted, mb: 1 }}>
                Customers are waiting for their plan to be activated.
              </Typography>
              <Button
                size="small"
                variant="contained"
                onClick={() => {
                  setOpen(false);
                  navigate(reviewPath);
                }}
              >
                Review now
              </Button>
            </Box>
          </Box>
        )}
      </Popover>
    </>
  );
}

export default Notifications;
